/**
 * 貼文排程設定 Modal — 薄殼，內容由 PostScheduleForm 提供。
 * MyPost 等獨立場景使用；
 * PostEditModal 不走 modal，直接 inline render PostScheduleForm。
 *
 * 確認/取消/移除排程按鈕統一放 Modal.Footer（全站 modal 慣例），
 * 透過 formRef 呼叫 PostScheduleForm 暴露的 confirm() / clear()。
 */
import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PostScheduleForm from './PostScheduleForm';

const PostScheduleModal = forwardRef(({ onConfirm }, ref) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [canConfirm, setCanConfirm] = useState(false);
  const formRef = useRef(null);

  useImperativeHandle(ref, () => ({
    show: (data = {}) => {
      setInitialValues({
        scheduledAt: data.scheduledAt,
      });
      setShow(true);
    },
    hide: () => setShow(false),
  }));

  const handleClose = () => setShow(false);

  const handleConfirm = (values) => {
    onConfirm?.(values);
    setShow(false);
  };

  const hadSchedule = !!initialValues.scheduledAt;

  return (
    // enforceFocus={false}：內含 DatePicker，flatpickr 的日曆掛在 document.body（modal 之外），
    // Modal 預設的焦點鎖會把日曆內時分／年份輸入框、月份 select 的焦點搶回來，導致完全無法輸入
    <Modal show={show} onHide={handleClose} centered enforceFocus={false}>
      <Modal.Header className="border-bottom pb-3" closeButton>
        <Modal.Title className="fw-bold">{t('schedule_post')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <PostScheduleForm
          ref={formRef}
          initialValues={initialValues}
          onConfirm={handleConfirm}
          onValidityChange={setCanConfirm}
        />
      </Modal.Body>
      <Modal.Footer className={`border-0 d-flex gap-2 ${hadSchedule ? 'justify-content-between' : 'justify-content-end'}`}>
        {hadSchedule && (
          <button type="button" className="btn btn-link text-danger px-0" onClick={() => formRef.current?.clear()}>
            {t('post_schedule_clear')}
          </button>
        )}
        <div className="d-flex gap-2">
          <button className="btn btn-neutral rounded-pill" onClick={handleClose}>
            {t('cancel')}
          </button>
          <button
            className="btn btn-primary rounded-pill"
            onClick={() => formRef.current?.confirm()}
            disabled={!canConfirm}
          >
            {t('confirm')}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
});

PostScheduleModal.displayName = 'PostScheduleModal';

export default PostScheduleModal;
